/**
 * HistoryView.tsx
 * ----------------
 * View for the History tab, available in the public portal.
 */

import { Spinner, SpinnerSize } from '@fluentui/react/lib/Spinner';
import { push } from 'connected-react-router';
import * as React from 'react';
import { useDispatch } from 'react-redux';

import { getActiveLiveSiteEvents, getResolvedLiveSiteEvents, setHistoryViewContext } from '../../actions/shared';
import { useImmutableSelector } from '../../hooks/useImmutableSelector';
import { LiveSiteEventSeverity } from '../../models/status';
import { TimeRangeValue } from '../../models/ui/status';
import {
    selectActiveLiveSiteEvents, selectFilteredGeographies, selectFilteredServices, selectFilteredSeverities,
    selectFilteredTime, selectGetActiveLiveSiteEventsError, selectGetActiveLiveSiteEventsStatus,
    selectGetResolvedLiveSiteEventsError, selectGetResolvedLiveSiteEventsStatus, selectResolvedLiveSiteEvents,
    selectSearch,
} from '../../selectors/shared/history';
import { ErrorCard } from '../shared/ErrorCard';
import { HistorySection } from '../shared/HistorySection';
import { isSetEquivalent } from '../../utilities/array';
import { getNormalizedGeographyValue, isValidGeography } from '../../utilities/geographies';
import { getNormalizedServiceIdentifier, isValidService } from '../../utilities/services';
import { getNormalizedSeverity, isValidSeverity } from '../../utilities/severities';
import { isUndefinedOrEmpty } from '../../utilities/strings';
import { getNormalizedTimeRangeValue, isValidTimeRangeValue } from '../../utilities/time';
import { buildHistorySearch, getSearchString } from '../../utilities/urls';

/**
 * Component properties
 */

interface HistoryViewProps {
    basePageTitle: string;
    geo?: string;
    historyUrl: string;
    liveSiteEventUrl: (id: number) => string;
    sev?: string;
    svc?: string;
    t?: string;
    /**
     * Only set to true when using the component in the update portion of the site.
     */
    useLatestData?: boolean;
}

/**
 * Private interfaces & types
 */

interface Context {
    geographies: string[];
    services: string[];
    severities: LiveSiteEventSeverity[];
    time: TimeRangeValue;
}

function getContext (
    geographies: string[],
    services: string[],
    severities: LiveSiteEventSeverity[],
    time: TimeRangeValue,
): Context {
    return { geographies, services, severities, time };
}

function hasContextChanged (previousContext: Context, currentContext: Context) {
    const {
        geographies: previousGeographies,
        services: previousServices,
        severities: previousSeverities,
        time: previousTime,
    } = previousContext;

    const { geographies, services, severities, time } = currentContext;

    return !isSetEquivalent(previousGeographies, geographies)
        || !isSetEquivalent(previousServices, services)
        || !isSetEquivalent(previousSeverities, severities)
        || time !== previousTime;
}

/**
 * View for the History tab, available in the public portal.
 * @param props Component properties
 * @returns Rendered content
 */
export function HistoryView (props: HistoryViewProps) {
    const { basePageTitle, geo, historyUrl, liveSiteEventUrl, sev, svc, t, useLatestData } = props;

    const requiresLatestData = useLatestData !== undefined ? useLatestData : false;
    const rawGeographies = geo !== undefined ? geo.split(',').map(g => g.trim()) : [];
    const rawServices = svc !== undefined ? svc.split(',').map(s => s.trim()) : [];
    const rawSeverities = sev !== undefined ? sev.split(',').map(s => s.trim()) : [];
    const rawTime = t !== undefined ? t : '';

    // Validation
    if (rawGeographies.some(geo => !isValidGeography(geo, true))) {
        console.warn('At least one of the geographies specified in "geo" parameter was not valid. These values will be ignored.');
    }

    if (rawServices.some(svc => !isValidService(svc, true))) {
        console.warn('At least one of the services specified in "svc" parameter was not valid. These values will be ignored.');
    }

    if (rawSeverities.some(sev => !isValidSeverity(sev, true))) {
        console.warn('At least one of the services specified in "sev" parameter was not valid. These values will be ignored.');
    }

    if (!isUndefinedOrEmpty(rawTime) && !isValidTimeRangeValue(rawTime, true)) {
        console.warn('"t" parameter was not valid. This value will be ignored.');
    }

    const geographies = rawGeographies
        .map(getNormalizedGeographyValue)
        .filter(geo => geo !== undefined)
        .map(geo => geo as string);

    const services = rawServices
        .map(getNormalizedServiceIdentifier)
        .filter(svc => svc !== null)
        .map(svc => svc as string);

    const severities = rawSeverities
        .map(getNormalizedSeverity)
        .filter(sev => sev !== undefined)
        .map(sev => sev as LiveSiteEventSeverity);

    const time = getNormalizedTimeRangeValue(rawTime) ?? 'Last7Days';

    // Action hooks
    const dispatch = useDispatch();

    // Application state hooks
    const activeLiveSiteEvents = useImmutableSelector(selectActiveLiveSiteEvents);
    const filteredGeographies = useImmutableSelector(selectFilteredGeographies);
    const filteredServices = useImmutableSelector(selectFilteredServices);
    const filteredSeverities = useImmutableSelector(selectFilteredSeverities);
    const filteredTime = useImmutableSelector(selectFilteredTime);
    const getActiveLiveSiteEventsError = useImmutableSelector(selectGetActiveLiveSiteEventsError);
    const getActiveLiveSiteEventsStatus = useImmutableSelector(selectGetActiveLiveSiteEventsStatus);
    const getResolvedLiveSiteEventsError = useImmutableSelector(selectGetResolvedLiveSiteEventsError);
    const getResolvedLiveSiteEventsStatus = useImmutableSelector(selectGetResolvedLiveSiteEventsStatus);
    const resolvedLiveSiteEvents = useImmutableSelector(selectResolvedLiveSiteEvents);
    const search = useImmutableSelector(selectSearch);

    // Callback hooks
    const onGeographiesChanged = React.useCallback((newValues: string[]) => {
        dispatch(push({
            pathname: historyUrl,
            search: getSearchString(buildHistorySearch(newValues, filteredServices, filteredSeverities, filteredTime)),
        }));
    }, [dispatch, filteredServices, filteredSeverities, filteredTime, historyUrl]);

    const onSearchChanged = React.useCallback((search: string) => {
        dispatch(
            setHistoryViewContext(filteredGeographies, filteredServices, filteredSeverities, filteredTime, search)
        );
    }, [dispatch, filteredGeographies, filteredServices, filteredSeverities, filteredTime]);

    const onServicesChanged = React.useCallback((newValues: string[]) => {
        dispatch(push({
            pathname: historyUrl,
            search: getSearchString(buildHistorySearch(filteredGeographies, newValues, filteredSeverities, filteredTime)),
        }));
    }, [dispatch, filteredGeographies, filteredSeverities, filteredTime, historyUrl]);

    const onSeveritiesChanged = React.useCallback((newValues: LiveSiteEventSeverity[]) => {
        dispatch(push({
            pathname: historyUrl,
            search: getSearchString(buildHistorySearch(filteredGeographies, filteredServices, newValues, filteredTime)),
        }));
    }, [dispatch, filteredGeographies, filteredServices, filteredTime, historyUrl]);

    const onTimeChanged = React.useCallback((newValue: TimeRangeValue) => {
        dispatch(push({
            pathname: historyUrl,
            search: getSearchString(buildHistorySearch(filteredGeographies, filteredServices, filteredSeverities, newValue)),
        }));
    }, [dispatch, filteredGeographies, filteredServices, filteredTime, historyUrl]);

    // Effect hooks
    // Effect: set page title to indicate this is the history page
    React.useEffect(() => {
        document.title = `${basePageTitle}: live site event history`;
    }, [basePageTitle]);

    // Update context with current filter values when appropriate
    const previousContext = getContext(filteredGeographies, filteredServices, filteredSeverities, filteredTime);
    const currentContext = getContext(geographies, services, severities, time);

    if (hasContextChanged(previousContext, currentContext)) {
        dispatch(setHistoryViewContext(geographies, services, severities, time, search));
    }

    // Reload active live site events if status for active live site events is none
    if (getActiveLiveSiteEventsStatus === 'none') {
        dispatch(getActiveLiveSiteEvents(requiresLatestData));
    }

    // Reload resolved live site events if status for resolved live site events is none
    if (getResolvedLiveSiteEventsStatus === 'none') {
        dispatch(getResolvedLiveSiteEvents(time, requiresLatestData));
    }

    // Failed state: show error card
    if (getActiveLiveSiteEventsStatus === 'failed' || getResolvedLiveSiteEventsStatus === 'failed') {
        return (
            <div className="history-view">
                <div className="history-view__error-card-container">
                    {getActiveLiveSiteEventsStatus === 'failed' &&
                        <ErrorCard
                            message={getActiveLiveSiteEventsError}
                            title="Failed to load active live site events" />}

                    {getActiveLiveSiteEventsStatus !== 'failed' && getResolvedLiveSiteEventsStatus === 'failed' &&
                        <ErrorCard
                            message={getResolvedLiveSiteEventsError}
                            title="Failed to load resolved live site events" />}
                </div>
            </div>
        );
    }

    // Loading state: show a spinner
    if (getActiveLiveSiteEventsStatus === 'inProgress' || getResolvedLiveSiteEventsStatus === 'inProgress') {
        return (
            <div className="history-view">
                <div className="history-view__loading-container">
                    <Spinner
                        className="history-view__spinner"
                        label="Loading live site events..."
                        size={SpinnerSize.large} />
                </div>
            </div>
        );
    }

    return (
        <HistorySection
            activeLiveSiteEvents={activeLiveSiteEvents}
            geographies={filteredGeographies}
            liveSiteEventUrl={liveSiteEventUrl}
            onGeographiesChanged={onGeographiesChanged}
            onSearchSubmitted={onSearchChanged}
            onServicesChanged={onServicesChanged}
            onSeveritiesChanged={onSeveritiesChanged}
            onTimeChanged={onTimeChanged}
            resolvedLiveSiteEvents={resolvedLiveSiteEvents}
            services={filteredServices}
            severities={filteredSeverities}
            time={filteredTime} />
    );
}
